import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid3x3, Dumbbell, Trophy, ChevronRight, Edit2, Trash2, AlertTriangle } from 'lucide-react';
import { ProTopBar } from '../components/ProTopBar';
import { useCoachPrograms, useTopPR, useDeleteCoachProgram } from '../lib/queries';
import { useRole } from '../lib/role';
import { ApiError } from '../lib/api';
import styles from './Dashboard.module.css';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function avatarColor(seed: string): string {
  const palette = ['#1d4ed8', '#7c3aed', '#0b8a3e', '#e85d00', '#0891b2', '#d64545', '#be185d', '#a87c00'];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
}

/**
 * Sprint 6.A — Workout (coach) overview.
 * Sostituisce ComingSoon. Mostra programmi attivi, esercizi più richiesti
 * derivati dai programmi, e PR recenti.
 */
export default function Workout() {
  const { role } = useRole();
  const navigate = useNavigate();
  const coachEnabled = role === 'coach';
  const { data: programs, isLoading: pLoading } = useCoachPrograms(coachEnabled);
  const { data: prData, isLoading: prLoading } = useTopPR(30, coachEnabled);
  const deleteMutation = useDeleteCoachProgram();
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleDelete = (programId: string) => {
    setDeleteError(null);
    void (async () => {
      try {
        await deleteMutation.mutateAsync(programId);
        setConfirmDeleteId(null);
      } catch (err: unknown) {
        const e = err as ApiError | Error;
        setDeleteError(e instanceof ApiError && e.details ? `${e.message} — ${e.details}` : e.message || String(err));
      }
    })();
  };

  const stats = useMemo(() => {
    const list = programs || [];
    return {
      active: list.filter((p) => p.status === 'active').length,
      draft: list.filter((p) => p.status === 'draft').length,
      paused: list.filter((p) => p.status === 'paused').length,
      done: list.filter((p) => p.status === 'completed').length,
    };
  }, [programs]);

  if (role !== 'coach') {
    return (
      <>
        <ProTopBar />
        <div style={{ padding: '4px 0 18px' }}>
          <div
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: 1.6,
              color: 'var(--mute)',
              textTransform: 'uppercase',
            }}
          >
            Studio Pro · Modulo Sport
          </div>
          <h1
            style={{
              fontFamily: 'var(--display)',
              fontWeight: 800,
              fontSize: 32,
              letterSpacing: -1,
              margin: '6px 0 0',
              lineHeight: 1.05,
            }}
          >
            Workout
          </h1>
        </div>
        <div className={styles.card}>
          <div className={styles.empty} style={{ padding: '48px 32px' }}>
            Il modulo Workout è disponibile solo per professionisti con ruolo Coach.
            Contatta il supporto per abilitare il ruolo Coach sul tuo account.
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <ProTopBar customCtaLabel="Nuovo programma" onCta={() => navigate('/programmi/new')} />

      <div style={{ padding: '4px 0 18px' }}>
        <div
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: 1.6,
            color: 'var(--mute)',
            textTransform: 'uppercase',
          }}
        >
          Studio Pro · Workout
        </div>
        <h1
          style={{
            fontFamily: 'var(--display)',
            fontWeight: 800,
            fontSize: 32,
            letterSpacing: -1,
            margin: '6px 0 0',
            lineHeight: 1.05,
          }}
        >
          Programmi &amp; PR
        </h1>
      </div>

      <div className={styles.kpis}>
        <div className={styles.kpi}>
          <div className={styles.kpiIconBg} style={{ background: 'var(--green-soft)', color: 'var(--green-deep)' }}>
            <Grid3x3 size={16} strokeWidth={2} />
          </div>
          <div className={styles.kpiLabel}>Programmi attivi</div>
          <div className={`${styles.kpiVal} tnum`}>{stats.active}</div>
        </div>
        <div className={styles.kpi}>
          <div className={styles.kpiIconBg} style={{ background: 'var(--orange-soft)', color: 'var(--orange-deep)' }}>
            <Dumbbell size={16} strokeWidth={2} />
          </div>
          <div className={styles.kpiLabel}>In bozza</div>
          <div className={`${styles.kpiVal} tnum`}>{stats.draft}</div>
        </div>
        <div className={styles.kpi}>
          <div className={styles.kpiIconBg} style={{ background: '#fff3d6', color: '#a87c00' }}>
            <Dumbbell size={16} strokeWidth={2} />
          </div>
          <div className={styles.kpiLabel}>In pausa</div>
          <div className={`${styles.kpiVal} tnum`}>{stats.paused}</div>
        </div>
        <div className={styles.kpi}>
          <div className={styles.kpiIconBg} style={{ background: 'var(--purple-soft)', color: 'var(--purple)' }}>
            <Trophy size={16} strokeWidth={2} />
          </div>
          <div className={styles.kpiLabel}>PR ultimi 30g</div>
          <div className={`${styles.kpiVal} tnum`}>{prData?.records.length ?? 0}</div>
        </div>
      </div>

      <div className={styles.gridMain}>
        <section className={styles.card}>
          <header className={styles.cardHeader}>
            <div>
              <div className={styles.cardTitle}>Programmi</div>
              <div className={styles.cardSub}>{programs?.length ?? 0} programmi · ordinati per stato</div>
            </div>
          </header>
          {pLoading ? (
            <div className={styles.empty}>Caricamento…</div>
          ) : !programs || programs.length === 0 ? (
            <div className={styles.empty}>Nessun programma creato. Inizia da "Nuovo programma".</div>
          ) : (
            <div className={styles.roster}>
              <table className={styles.rosterTable}>
                <thead>
                  <tr>
                    <th style={{ width: '32%' }}>Atleta</th>
                    <th>Programma</th>
                    <th>Goal</th>
                    <th>Settimane</th>
                    <th>Progress</th>
                    <th style={{ width: 40 }} aria-label="Azioni" />
                  </tr>
                </thead>
                <tbody>
                  {programs.slice(0, 10).map((p) => {
                    const total = p.sessions_total || 0;
                    const done = p.sessions_done || 0;
                    const pct = total > 0 ? Math.round((done / total) * 100) : 0;

                    return (
                      <tr key={p.id}>
                        <td>
                          <div className={styles.pt}>
                            <div className={styles.ptAv} style={{ background: avatarColor(p.athlete_id) }}>
                              {initials(p.athlete_name)}
                            </div>
                            <div>
                              <div className={styles.ptName}>{p.athlete_name}</div>
                              <div className={styles.ptMeta}>{p.status.toUpperCase()}</div>
                            </div>
                          </div>
                        </td>
                        <td><span className={styles.num} style={{ color: 'var(--ink)' }}>{p.name}</span></td>
                        <td><span className={styles.num}>{p.goal || '—'}</span></td>
                        <td><span className={styles.num}>{p.weeks_total}</span></td>
                        <td>
                          <div className={styles.adhCell}>
                            <span className={styles.num}>{done}/{total}</span>
                            <div className={styles.adhBar}>
                              <div style={{ width: `${pct}%`, background: 'var(--orange)' }} />
                            </div>
                          </div>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <button
                            type="button"
                            className={styles.rowAction}
                            aria-label="Modifica"
                            onClick={() => navigate(`/programmi/${p.id}/edit`)}
                            title="Modifica programma"
                          >
                            <Edit2 size={14} strokeWidth={2} />
                          </button>
                          {p.status !== 'archived' && (
                            <button
                              type="button"
                              className={styles.rowAction}
                              aria-label="Archivia"
                              onClick={() => setConfirmDeleteId(p.id)}
                              title="Archivia programma"
                              style={{ marginLeft: 4 }}
                            >
                              <Trash2 size={14} strokeWidth={2} />
                            </button>
                          )}
                          <button
                            type="button"
                            className={styles.rowAction}
                            aria-label="Apri"
                            onClick={() => navigate(`/programmi/${p.id}/edit`)}
                            style={{ marginLeft: 4 }}
                          >
                            <ChevronRight size={16} strokeWidth={2} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className={styles.card}>
          <header className={styles.cardHeader}>
            <div>
              <div className={styles.cardTitle}>Top PR · 30 giorni</div>
              <div className={styles.cardSub}>{prData?.records.length ?? 0} record</div>
            </div>
          </header>
          <div className={styles.prList}>
            {prLoading ? (
              <div className={styles.empty}>Caricamento…</div>
            ) : !prData || prData.records.length === 0 ? (
              <div className={styles.empty}>Ancora nessun PR registrato.</div>
            ) : (
              prData.records.slice(0, 6).map((r, i) => (
                <div key={`${r.athlete_id}-${r.exercise_name}-${i}`} className={styles.prRow}>
                  <div className={styles.prRank}>{String(i + 1).padStart(2, '0')}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div className={styles.ptAv} style={{ background: avatarColor(r.athlete_id), width: 28, height: 28, fontSize: 10 }}>
                      {initials(r.name)}
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{r.name}</div>
                      <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--mute)', marginTop: 1, textTransform: 'uppercase' }}>
                        {r.exercise_name} · {r.kind.toUpperCase()}
                      </div>
                    </div>
                  </div>
                  <div className={styles.prValue}>
                    {r.value_kg ?? r.value_reps ?? '—'}
                    <span className={styles.prValueUnit}>{r.value_kg !== null ? 'kg' : r.value_reps ? 'r' : ''}</span>
                  </div>
                  <span className={styles.prDelta}>PR</span>
                </div>
              ))
            )}
          </div>
        </section>
      </div>

      {/* Confirm delete modal */}
      {confirmDeleteId && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => {
            if (!deleteMutation.isPending) {
              setConfirmDeleteId(null);
              setDeleteError(null);
            }
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--surface)',
              borderRadius: 12,
              padding: 24,
              maxWidth: 440,
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <AlertTriangle size={20} color="var(--orange-deep)" />
              <h3 style={{ fontFamily: 'var(--display)', fontSize: 17, fontWeight: 700, margin: 0 }}>
                Archivia programma?
              </h3>
            </div>
            <p style={{ color: 'var(--ink-2)', fontSize: 13.5, lineHeight: 1.5, margin: '0 0 14px' }}>
              Il programma sarà spostato in stato <b>archiviato</b>. L'atleta non lo vedrà più nelle sessioni
              attive. L'azione è reversibile riportando lo stato a "Attivo".
            </p>
            {deleteError && (
              <div className={styles.errorBox} style={{ margin: '0 0 14px' }}>
                {deleteError}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                onClick={() => {
                  if (!deleteMutation.isPending) {
                    setConfirmDeleteId(null);
                    setDeleteError(null);
                  }
                }}
                disabled={deleteMutation.isPending}
                style={{
                  border: '1px solid var(--line)',
                  background: 'var(--surface)',
                  color: 'var(--ink)',
                  padding: '8px 14px',
                  borderRadius: 7,
                  fontWeight: 600,
                  cursor: deleteMutation.isPending ? 'not-allowed' : 'pointer',
                }}
              >Annulla</button>
              <button
                type="button"
                onClick={() => handleDelete(confirmDeleteId)}
                disabled={deleteMutation.isPending}
                style={{
                  border: 0,
                  background: 'var(--red)',
                  color: '#fff',
                  padding: '8px 14px',
                  borderRadius: 7,
                  fontWeight: 700,
                  cursor: deleteMutation.isPending ? 'not-allowed' : 'pointer',
                }}
              >{deleteMutation.isPending ? 'Archiviazione…' : 'Archivia'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
